/*!
 * Pampas Mood · B2B contact tracking · v2.0.3 (T-B12, 2026-10-12) · 2.0.2 + redirect requirement (redirectCount ≥ 1)
 * Standalone theme asset. No dependencies. No personal data is read into the event or pushed.
 * Independent of pampas_b2b_lead, "B2B MAIL"/"B2B WHATSAPP" and every other script on the page.
 *
 * Pushes ONE event name to the top-window window.dataLayer (read by GTM-NTJPTG2, which runs in the top window):
 *   {event:'b2b_contact', contact_method:'email',    status:'success', page_type:'b2b'}   B2B contact form accepted by Shopify
 *   {event:'b2b_contact', contact_method:'whatsapp', status:'click',   page_type:'b2b'}   click on the B2B section's own WhatsApp CTA
 *
 * Email success = ALL of:
 *   1. this tab made a valid submit attempt on the B2B form ≤15 min ago (sessionStorage marker; Shopify's captcha cancels the visible
 *      submit event and posts natively, so the attempt — not the event's defaultPrevented state — is what we can observe);
 *   2. the browser reports a fresh navigation (Navigation Timing L2 type 'navigate'; not reload / back-forward; a missing entry is NOT
 *      counted) that went through at least one same-origin HTTP redirect (redirectCount ≥ 1: POST /contact → 302, incl. /challenge;
 *      a link click or location.assign has 0), from the same origin;
 *   3. Shopify's redirect carries contact_posted=true (URL captured at script start, before pampas-b2b.js strips it; the
 *      navigation entry's original URL is the fallback);
 *   4. Shopify server-rendered the success block for that form and no error block.
 *   The marker is consumed on the first success page → exactly once per submission.
 * WhatsApp click = click inside the B2B section on a wa.me / api.whatsapp.com link that the page did not cancel; 2 s debounce.
 *   Navigation to WhatsApp is never blocked or delayed.
 *
 * Consent: by default the event is pushed regardless of Shopify consent, matching how GTM-NTJPTG2 itself is loaded today
 * (unconditionally, no Consent Mode). Consent for the tags that consume it must be enforced in GTM.
 * Set REQUIRE_ANALYTICS_CONSENT = true to push only when Shopify.customerPrivacy.analyticsProcessingAllowed() is true.
 */
(function () {
  'use strict';
  if (window.__pmbB2BTracking) return;           // double-include / double-init guard
  window.__pmbB2BTracking = '2.0.3';

  var REQUIRE_ANALYTICS_CONSENT = false;
  var FORM_SEL = 'form[data-email-form], form.pmb-email-form';
  var MARKER_KEY = 'pmb_b2b_attempt_v2';
  var MAX_AGE_MS = 15 * 60 * 1000;
  var WA_DEBOUNCE_MS = 2000;
  var WA_HREF = /^https:\/\/(wa\.me|api\.whatsapp\.com)\//;

  // ---- URL at script start (pampas-b2b.js later strips contact_posted with history.replaceState)
  var startHref = location.href;
  // The navigation entry is read at check time (DOMContentLoaded or later): WebKit does not expose it at document start.
  function navEntry() { try { return (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || null; } catch (e) { return null; } }
  function postedIn(url) { try { return new URL(url, location.href).searchParams.get('contact_posted') === 'true'; } catch (e) { return false; } }

  // ---- helpers
  function ss() { try { return window.sessionStorage; } catch (e) { return null; } }
  function readMarker() { var s = ss(); if (!s) return null; try { return JSON.parse(s.getItem(MARKER_KEY) || 'null'); } catch (e) { return null; } }
  function writeMarker(v) { var s = ss(); if (!s) return; try { s.setItem(MARKER_KEY, JSON.stringify(v)); } catch (e) {} }
  function clearMarker() { var s = ss(); if (!s) return; try { s.removeItem(MARKER_KEY); } catch (e) {} }
  function sameOriginReferrer() { try { return !!document.referrer && new URL(document.referrer).origin === location.origin; } catch (e) { return false; } }
  function sectionOf(el) { return (el && el.closest && el.closest('.shopify-section')) || null; }
  function b2bSection() { return sectionOf(document.querySelector(FORM_SEL)); }
  function consentOk() {
    if (!REQUIRE_ANALYTICS_CONSENT) return true;
    try { var p = window.Shopify && window.Shopify.customerPrivacy; return !!(p && p.analyticsProcessingAllowed && p.analyticsProcessingAllowed() === true); } catch (e) { return false; }
  }
  function push(method, status) {
    if (!consentOk()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'b2b_contact', contact_method: method, status: status, page_type: 'b2b' });
  }

  // ---- 1. valid submit attempt on the B2B form → marker (window bubble phase; never cancels or delays anything)
  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches(FORM_SEL)) return;
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
    writeMarker({ f: form.id || '', t: Date.now() });
  }, false);

  // ---- 2. success check, once per page load
  function checkSuccess() {
    var nav = navEntry();
    var postedAtLoad = postedIn(startHref) || !!(nav && nav.name && postedIn(nav.name));
    var m = readMarker();
    if (!postedAtLoad || !m) return;                  // not a success URL, or manual / shared ?contact_posted=true
    clearMarker();                                    // consume first: a marker can confirm at most one page
    if (!(Date.now() - m.t >= 0 && Date.now() - m.t <= MAX_AGE_MS)) return;
    // Fail-closed: browsers without the Navigation Timing L2 entry are not counted (reload / back-forward can't be told apart there).
    if (!nav || nav.type !== 'navigate') return;
    // Shopify answers the accepted POST /contact with a same-origin 302; a link click / location.assign / typed URL has no redirect.
    if (!(nav.redirectCount >= 1)) return;
    if (!sameOriginReferrer()) return;                // typed / pasted URL
    var form = m.f ? document.getElementById(m.f) : null;
    if (!form || !form.matches(FORM_SEL)) return;
    var section = sectionOf(form) || document;
    if (!section.querySelector('[data-email-success]')) return;   // Shopify did not render success
    if (section.querySelector('[data-email-errors]')) return;     // Shopify rendered errors
    push('email', 'success');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkSuccess, { once: true });
  else checkSuccess();

  // bfcache restore does not re-run this script, so nothing is re-pushed.

  // ---- 3. WhatsApp CTA inside the B2B section only
  var lastWa = 0;
  window.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || !WA_HREF.test(a.getAttribute('href') || '')) return;
    var section = b2bSection();
    if (!section || !section.contains(a)) return;     // dock / footer / bottom nav are outside the B2B section
    if (e.defaultPrevented) return;                   // the page cancelled it (e.g. invalid selection)
    var now = Date.now();
    if (now - lastWa < WA_DEBOUNCE_MS) return;
    lastWa = now;
    push('whatsapp', 'click');
  }, false);
})();
